const cascadeDataToContentNode = (data, contentNode) => {
    if (data === undefined || data === null) {
        return { ...contentNode, data };
    }
    const isObject = typeof data === 'object' && data !== null;
    switch (contentNode.kind) {
        case 'INPUT_VALUE': {
            const updatedOfType = cascadeDataToContentNode(data, contentNode.ofType);
            return {
                ...contentNode,
                data,
                ofType: updatedOfType,
            };
        }
        case 'INPUT_OBJECT': {
            const updatedInputFields = contentNode.fields.map(inputField => {
                const fieldKey = 'key' in inputField ? inputField.key : undefined;
                const fieldData = isObject && fieldKey ? data[fieldKey] : undefined;
                return cascadeDataToContentNode(fieldData, inputField);
            });
            return {
                ...contentNode,
                data,
                fields: updatedInputFields,
            };
        }
        case 'LIST': {
            if (Array.isArray(data)) {
                const updatedItems = data.map((itemData, index) => {
                    const existingItem = contentNode.items?.[index];
                    if (existingItem) {
                        return cascadeDataToContentNode(itemData, existingItem);
                    }
                    return cascadeDataToContentNode(itemData, contentNode.ofType);
                });
                return {
                    ...contentNode,
                    data,
                    items: updatedItems,
                };
            }
            return { ...contentNode, data };
        }
        case 'SCALAR':
        case 'ENUM':
            return { ...contentNode, data };
        default:
            return { ...contentNode, data };
    }
};
const setInitialVariablesOnContentNodes = (variables) => (contentNodes) => contentNodes.map(contentNode => {
    if ('key' in contentNode) {
        const variableData = variables[contentNode.key];
        return cascadeDataToContentNode(variableData, contentNode);
    }
    return contentNode;
});
const updateVariablesOnContentNodes = (variables) => (contentNodes) => {
    return contentNodes.map(contentNode => {
        if ('key' in contentNode && variables.hasOwnProperty(contentNode.key)) {
            const variableData = variables[contentNode.key];
            return cascadeDataToContentNode(variableData, contentNode);
        }
        return contentNode;
    });
};
const updateVariableOnContentNode = (locator, value) => (contentNode) => {
    if (locator.length === 0) {
        return cascadeDataToContentNode(value, contentNode);
    }
    const [currentMatcher, ...restLocator] = locator;
    switch (contentNode.kind) {
        case 'INPUT_VALUE': {
            if ('key' in contentNode) {
                const [matcherType, matcherValue] = currentMatcher;
                if (matcherType === 'key' && matcherValue === contentNode.key) {
                    const newOfType = updateVariableOnContentNode(restLocator, value)(contentNode.ofType);
                    if (newOfType !== contentNode.ofType) {
                        return {
                            ...contentNode,
                            ofType: newOfType,
                            data: newOfType.data,
                        };
                    }
                }
            }
            return contentNode;
        }
        case 'INPUT_OBJECT': {
            const [matcherType] = currentMatcher;
            if (matcherType === 'key') {
                let wasChanged = false;
                const newInputFields = contentNode.fields.map(field => {
                    const updatedField = updateVariableOnContentNode(locator, value)(field);
                    if (updatedField !== field) {
                        wasChanged = true;
                    }
                    return updatedField;
                });
                if (wasChanged) {
                    const newData = newInputFields.reduce((acc, field) => {
                        if ('key' in field) {
                            acc[field.key] = field.data;
                        }
                        return acc;
                    }, {});
                    return {
                        ...contentNode,
                        fields: newInputFields,
                        data: newData,
                    };
                }
            }
            return contentNode;
        }
        case 'LIST': {
            const [matcherType, matcherValue] = currentMatcher;
            if (matcherType === 'index') {
                const index = matcherValue;
                const items = contentNode.items ?? [];
                if (index >= 0 && index < items.length) {
                    const updatedItem = updateVariableOnContentNode(restLocator, value)(items[index]);
                    if (updatedItem !== items[index]) {
                        const newItems = [...items];
                        newItems[index] = updatedItem;
                        const newData = newItems.map(item => item.data);
                        return {
                            ...contentNode,
                            items: newItems,
                            data: newData,
                        };
                    }
                }
            }
            return contentNode;
        }
        default:
            return contentNode;
    }
};
const getVariablesFromContentNodes = (contentNodes) => {
    return contentNodes.reduce((acc, contentNode) => {
        if ('key' in contentNode) {
            acc[contentNode.key] = contentNode.data;
        }
        return acc;
    }, {});
};
export const QueryVariablesUtils = {
    setInitialVariablesOnContentNodes,
    updateVariablesOnContentNodes,
    updateVariableOnContentNode,
    getVariablesFromContentNodes,
};
//# sourceMappingURL=QueryVariablesUtils.js.map